import { useId, type FormEvent } from "react";
import { ACCOUNT_KINDS, type AccountKind, type LedgerAccount, type LedgerAccountInput } from "@/api/books";
import { RequestError } from "@/components/RequestError";
import { Checkbox } from "@/components/ui/checkbox";
import { SelectField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type LedgerAccountFormProps = {
  initial: LedgerAccount | null;
  onSubmit: (input: LedgerAccountInput) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
};

const GROUPS = [
  "Cash and cash equivalents",
  "Trade receivables",
  "Other current assets",
  "Fixed assets",
  "Trade payables",
  "Other current liabilities",
  "Short-term borrowings",
  "Short-term provisions",
  "Share capital",
  "Reserves and surplus",
  "Revenue from operations",
  "Other income",
  "Employee benefits expense",
  "Finance costs",
  "Depreciation and amortisation",
  "Other expenses",
  "Tax expense",
];

/** An account of the chart. A system account keeps its code and kind. */
export function LedgerAccountForm({ initial, onSubmit, onCancel, onReload }: LedgerAccountFormProps) {
  const id = useId();
  const system = initial?.system_key !== null && initial?.system_key !== undefined;
  const { values, set } = useFormValues({
    code: initial?.code ?? "",
    name: initial?.name ?? "",
    kind: (initial?.kind ?? "expense") as AccountKind,
    schedule_group: initial?.schedule_group ?? "Other expenses",
    archived: initial?.archived ?? false,
  });
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        code: values.code.trim(),
        name: values.name.trim(),
        kind: values.kind,
        schedule_group: values.schedule_group.trim(),
        archived: values.archived,
      }),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          id={`${id}-code`}
          label="Code"
          value={values.code}
          onChange={(v) => set("code", v)}
          error={errors.code}
          disabled={system}
          hint="Four digits: 1xxx assets, 2xxx liabilities, 3xxx equity, 4xxx income, 5xxx expenses"
        />
        <TextField
          id={`${id}-name`}
          label="Name"
          value={values.name}
          onChange={(v) => set("name", v)}
          error={errors.name}
        />
        <SelectField
          id={`${id}-kind`}
          label="Kind"
          value={values.kind}
          onChange={(v) => set("kind", v as AccountKind)}
          options={ACCOUNT_KINDS}
          error={errors.kind}
          disabled={system}
        />
        <SelectField
          id={`${id}-group`}
          label="Schedule III group"
          value={values.schedule_group}
          onChange={(v) => set("schedule_group", v)}
          options={GROUPS.map((g) => ({ value: g, label: g }))}
          error={errors.schedule_group}
        />
      </div>
      {initial && !system && (
        <Checkbox
          id={`${id}-archived`}
          label="Archived (no new entries; needs a zero balance)"
          checked={values.archived}
          onChange={(v) => set("archived", v)}
        />
      )}
      <FormActions saving={saving} submitLabel={initial ? "Save account" : "Add account"} onCancel={onCancel} />
    </form>
  );
}
